// Order-DB-Hooks. RLS gilt:
// - Kunden sehen nur eigene
// - Backoffice (admin/buero/dispatch) sieht alle

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type OrderStatus = "draft" | "placed" | "planned" | "in_delivery" | "delivered" | "failed" | "cancelled";
export type PaymentMethod = "cash" | "stripe" | "paypal" | "sepa" | "invoice";
export type OrderSource = "app" | "phone" | "abo" | "addon";

export interface Order {
  id: string;
  order_number: string | null;
  customer_id: string;
  address_id: string;
  delivery_date: string;       // YYYY-MM-DD
  status: OrderStatus;
  payment_method: PaymentMethod;
  source: OrderSource;
  subtotal_cents: number;
  delivery_fee_cents: number;
  total_cents: number;
  notes: string | null;
  placed_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  qty: number;
  unit_price_cents: number;
  source: OrderSource;
}

export interface OrderWithJoins extends Order {
  customer: { display_name: string; phone: string | null; email?: string | null } | null;
  address: { street: string; zip: string; city: string; geo_lat?: number | null; geo_lng?: number | null } | null;
  items?: OrderItem[];
}

// ---------- Listen / Detail ----------

export function useOrders(filter: {
  status?: OrderStatus[];
  date?: string;
  customerId?: string;
} = {}) {
  return useQuery({
    queryKey: ["orders", filter],
    queryFn: async () => {
      let q = supabase
        .from("orders")
        .select(`
          *,
          customer:customers(display_name, phone),
          address:customer_addresses(street, zip, city)
        `)
        .order("delivery_date", { ascending: true })
        .order("created_at", { ascending: false });
      if (filter.status?.length) q = q.in("status", filter.status);
      if (filter.date) q = q.eq("delivery_date", filter.date);
      if (filter.customerId) q = q.eq("customer_id", filter.customerId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as OrderWithJoins[];
    },
  });
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: ["order", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          *,
          customer:customers(display_name, phone, email),
          address:customer_addresses(street, zip, city, geo_lat, geo_lng),
          items:order_items(*)
        `)
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as OrderWithJoins;
    },
  });
}

export function useMyOrders() {
  return useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          *,
          address:customer_addresses(street, zip, city)
        `)
        .order("delivery_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as OrderWithJoins[];
    },
  });
}

// ---------- Create ----------

export interface CreateOrderInput {
  customer_id: string;
  address_id: string;
  delivery_date: string;
  payment_method: PaymentMethod;
  source: OrderSource;
  notes?: string;
  items: Array<{ product_id: string; qty: number; unit_price_cents: number }>;
  delivery_fee_cents?: number;
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateOrderInput) => {
      const subtotal = input.items.reduce((a, i) => a + i.qty * i.unit_price_cents, 0);
      const fee = input.delivery_fee_cents ?? 0;
      const total = subtotal + fee;

      // 1) Order anlegen
      const { data: order, error } = await supabase
        .from("orders")
        .insert({
          customer_id: input.customer_id,
          address_id: input.address_id,
          delivery_date: input.delivery_date,
          payment_method: input.payment_method,
          source: input.source,
          status: "placed",
          notes: input.notes ?? null,
          subtotal_cents: subtotal,
          delivery_fee_cents: fee,
          total_cents: total,
        })
        .select()
        .single();
      if (error) throw error;

      // 2) Items
      const itemRows = input.items.map((i) => ({
        order_id: order.id,
        product_id: i.product_id,
        qty: i.qty,
        unit_price_cents: i.unit_price_cents,
        source: input.source,
      }));
      const { error: itemsErr } = await supabase.from("order_items").insert(itemRows);
      if (itemsErr) throw itemsErr;

      // 3) order_number generieren (HS-YYYY-NNNN) — simpler Client-Hack basierend auf created_at + suffix
      const yr = new Date(order.created_at).getFullYear();
      const short = order.id.split("-")[0].toUpperCase();
      const orderNumber = `HS-${yr}-${short}`;
      await supabase.from("orders").update({ order_number: orderNumber }).eq("id", order.id);

      return { ...order, order_number: orderNumber } as Order;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["my-orders"] });
    },
  });
}

// ---------- Update ----------

export function useUpdateOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: OrderStatus }) => {
      const { data, error } = await supabase
        .from("orders")
        .update({ status })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as Order;
    },
    onSuccess: (o) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order", o.id] });
      qc.invalidateQueries({ queryKey: ["my-orders"] });
    },
  });
}

// ---------- Helpers ----------

export const STATUS_LABEL: Record<OrderStatus, string> = {
  draft: "Entwurf",
  placed: "Bestellt",
  planned: "Eingeplant",
  in_delivery: "In Lieferung",
  delivered: "Geliefert",
  failed: "Fehlgeschlagen",
  cancelled: "Storniert",
};

export const STATUS_PILL: Record<OrderStatus, string> = {
  draft: "pill bg-hof-100 text-hof-800",
  placed: "pill-info",
  planned: "pill bg-wiese-100 text-wiese-800",
  in_delivery: "pill bg-sonne-100 text-sonne-800",
  delivered: "pill-success",
  failed: "pill-danger",
  cancelled: "pill bg-hof-200 text-hof-700",
};

export const PAYMENT_LABEL: Record<PaymentMethod, string> = {
  cash: "Bar bei Lieferung",
  stripe: "Karte/Apple Pay",
  paypal: "PayPal",
  sepa: "SEPA-Lastschrift",
  invoice: "Rechnung",
};
